// Обновленная версия CurrencyContext для получения валюты через API

import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';
import { useTelegram } from './TelegramContext';

const CurrencyContext = createContext();

export const CurrencyProvider = ({ children }) => {
  const [userCurrency, setUserCurrency] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { getUserId } = useTelegram();

  // Загрузка валюты пользователя при инициализации
  useEffect(() => {
    const loadUserCurrency = async () => {
      try {
        setIsLoading(true);
        const userId = getUserId();
        
        if (!userId) {
          console.log('User ID not available yet, waiting...');
          return;
        }
        
        // Проверяем наличие валюты в localStorage для быстрого отображения
        const cachedCurrency = localStorage.getItem('user_currency');
        if (cachedCurrency) {
          console.log('Using cached currency from localStorage:', cachedCurrency);
          setUserCurrency(cachedCurrency);
        }
        
        try {
          // Важно: используем endpoint /user_settings вместо /get_preferred_currency
          // В соответствии с серверным API в server.py
            const response = await api.get(`/get_preferred_currency`, {
              params: { user_id: userId }
            });
  
          
          // Сервер возвращает объект с полем "currency" вместо "preferred_currency"
          if (response.data && response.data.currency) {
            const fetchedCurrency = response.data.currency;
            console.log('Fetched currency from API:', fetchedCurrency);
            
            // Обновляем только если значение изменилось
            if (fetchedCurrency !== userCurrency) {
              setUserCurrency(fetchedCurrency);
              // Кэшируем актуальное значение
              localStorage.setItem('user_currency', fetchedCurrency);
            }
          } else {
            // Если валюта не найдена в ответе, используем UAH как значение по умолчанию
            console.log('No currency in API response, defaulting to UAH');
            if (!userCurrency) {
              setUserCurrency('UAH');
              localStorage.setItem('user_currency', 'UAH');
            }
          }
        } catch (apiError) {
          console.error('API request failed:', apiError);
          // Используем localStorage или устанавливаем UAH при ошибке API
          if (!userCurrency && cachedCurrency) {
            console.log('Using cached currency as fallback');
            setUserCurrency(cachedCurrency);
          } else if (!userCurrency) {
            console.log('Error occurred, defaulting to UAH');
            setUserCurrency('UAH');
            localStorage.setItem('user_currency', 'UAH');
          }
        }
      } catch (error) {
        console.error('Error loading user currency:', error);
        // При ошибке используем UAH как значение по умолчанию если нет кэшированного значения
        if (!userCurrency) {
          console.log('Error occurred, defaulting to UAH');
          setUserCurrency('UAH');
          localStorage.setItem('user_currency', 'UAH');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadUserCurrency();
    
    // Устанавливаем интервал для периодической синхронизации
    const intervalId = setInterval(loadUserCurrency, 60000); // 1 минута
    
    return () => clearInterval(intervalId);
  }, [getUserId]);

  // Функция для обновления валюты пользователя
  const updateUserCurrency = async (newCurrency) => {
    try {
      const userId = getUserId();
      
      if (!userId) {
        console.error('Cannot update currency: User ID not available');
        return false;
      }
      
      // Оптимистичное обновление UI
      setUserCurrency(newCurrency);
      localStorage.setItem('user_currency', newCurrency);
      
      // Отправка на сервер используя правильный endpoint и структуру данных
      const response = await api.post('/set_preferred_currency', {
        user_id: userId,
        currency: newCurrency
      });
      
      if (response.data && response.data.success) {
        console.log('Currency successfully updated to:', newCurrency);
        return true;
      } else {
        // Откат при ошибке
        const previousCurrency = localStorage.getItem('user_currency') || 'UAH';
        setUserCurrency(previousCurrency);
        console.error('Failed to update currency on server');
        return false;
      }
    } catch (error) {
      console.error('Error updating user currency:', error);
      // Откат при ошибке
      const previousCurrency = localStorage.getItem('user_currency') || 'UAH';
      setUserCurrency(previousCurrency);
      return false;
    }
  };

  return (
    <CurrencyContext.Provider value={{ 
      userCurrency, 
      updateUserCurrency,
      isLoading 
    }}>
      {children}
    </CurrencyContext.Provider>
  );
};

export const useCurrency = () => useContext(CurrencyContext);